import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { Logo } from 'components/Logo';
import { Button } from 'components/ui/Button';
import { Input } from 'components/ui/Input';
import { Icon } from 'components/ui';
import CirculerShadow from 'components/ui/CirculerShadow';
import { saveSignupDetails } from 'hooks/useSignupExtraDetails';
import { BACK_TO_WEBSITE_LINK } from 'config/constants';

interface SignupFormData {
  firstName: string;
  lastName: string;
  company: string;
  email: string;
  password: string;
  confirmPassword: string;
}

export const Signup: React.FC = () => {
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const { signUp } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<SignupFormData>();

  const password = watch('password');

  const onSubmit = async (data: SignupFormData) => {
    setError('');
    setSuccess('');
    setLoading(true);

    // Save signup details to localStorage as fallback (Supabase doesn't persist metadata during signup)
    saveSignupDetails({
      firstName: data.firstName,
      lastName: data.lastName,
      companyName: data.company,
    });

    // Prepare user metadata (try to pass it anyway, might work in some cases)
    const metadata = {
      first_name: data.firstName,
      last_name: data.lastName,
      company_name: data.company,
    };

    const { error } = await signUp(data.email, data.password, metadata);

    if (error) {
      setError(error.message);
      setLoading(false);
    } else {
      // Signup successful, show success message
      setSuccess(
        'Account created successfully! Please check your email to verify your account, then login.',
      );
      setLoading(false);
      setTimeout(() => {
        navigate('/login');
      }, 3500);
    }
  };

  return (
    <div className="min-h-screen relative flex flex-col items-center justify-center bg-gray-100">
      <div
        className="w-full justify-between fixed top-0 max-w-7xl mx-auto z-10 flex pt-6 px-6"
        onClick={() => {
          window.open(BACK_TO_WEBSITE_LINK, '_blank');
        }}
      >
        <Logo />
        <a
          href={BACK_TO_WEBSITE_LINK}
          className="text-sm font-medium text-gray-600 hover:text-primary transition-colors"
        >
          ← Back to Website
        </a>
      </div>

      <div className="flex flex-col items-center mb-12 px-4 sm:px-6">
        <div className="my-4 py-6 text-center">
          <h1 className="text-4xl font-medium">Create your account</h1>
          <p className="text-xs mt-2">Start building your AI-powered marketing workspace.</p>
        </div>
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-8 sm:p-10">
          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-3 ">
            {/* Name fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Input
                  label="First Name"
                  {...register('firstName', { required: 'First name is required' })}
                  type="text"
                  placeholder="First Name"
                  error={errors.firstName?.message}
                />
              </div>
              <div>
                <Input
                  label="Last Name"
                  {...register('lastName', { required: 'Last name is required' })}
                  type="text"
                  placeholder="Last Name"
                  error={errors.lastName?.message}
                />
              </div>
            </div>

            {/* Company name */}
            <div>
              <Input
                label="Company name"
                {...register('company', { required: 'Company name is required' })}
                type="text"
                placeholder="Company name"
                error={errors.company?.message}
              />
            </div>

            {/* Email */}
            <div>
              <Input
                label="Email"
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
                type="email"
                placeholder="Email"
                error={errors.email?.message}
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <Input
                {...register('password', {
                  required: 'Password is required',
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters',
                  },
                })}
                type="password"
                placeholder="Password"
                error={errors.password?.message}
              />
            </div>

            {/* Confirm Password */}
            <div>
              <Input
                label="Confirm password"
                {...register('confirmPassword', {
                  required: 'Please confirm your password',
                  validate: (value) => value === password || 'Passwords do not match',
                })}
                type="password"
                placeholder="Confirm Password"
                error={errors.confirmPassword?.message}
              />
            </div>

            {/* Error and Success Messages */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs">
                {error}
              </div>
            )}

            {success && (
              <div className="p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg text-xs">
                {success}
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading || !!success}
              variant="primary"
              size="sm"
              className="w-full h-12 rounded-lg text-base font-medium transition-all hover:shadow-lg"
            >
              {loading ? 'Creating account...' : success ? 'Redirecting to login...' : 'Sign up'}
            </Button>
          </form>

          {/* Separator */}
          <div className="flex items-center my-4">
            <div className="flex-1 h-px bg-gray-300" />
            <span className="px-4 text-sm text-gray-500 font-medium">or</span>
            <div className="flex-1 h-px bg-gray-300" />
          </div>

          {/* Social Auth Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button
              variant="secondary"
              className="shadow-none rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 font-medium transition-all hover:shadow-sm text-sm px-4"
            >
              <span className="flex items-center justify-center gap-2">
                <Icon type="google" className="w-4" />
                <span>Continue with Google</span>
              </span>
            </Button>
            <Button
              variant="secondary"
              className="shadow-none rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 font-medium transition-all hover:shadow-sm text-sm px-4"
            >
              <span className="flex items-center justify-center gap-2">
                <Icon type="linkedin" className="w-4" />
                <span>Continue with LinkedIn</span>
              </span>
            </Button>
          </div>

          {/* Sign In Link */}
          {!success && (
            <div className="text-center mt-8">
              <p className="mt-4 text-center text-xs text-gray-600 flex justify-center items-center gap-2">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="text-link font-medium underline flex gap-1 items-center justify-center"
                >
                  Sign in <Icon type="rightArrow" className="w-3 h-3 font-thin" />
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="md:absolute hidden -top-30 right-0 ">
        <CirculerShadow className="" />
      </div>
    </div>
  );
};
